import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

// --- PREMIUM EDITORIAL DATA ---
const FEATURES = [
{
  id: '01',
  title: 'Portfolio Command',
  subtitle: 'Unified Unit & Lease Management',
  desc: 'Observe your entire estate from a single, uncompromised vantage point. Automated renewals and expiry alerts keep your operations permanently ahead of the curve, eliminating manual oversight.',
  // High-end abstract/architectural images from Unsplash
  image: 'https://images.unsplash.com/photo-1600607686527-6fb886090705?auto=format&fit=crop&w=2000&q=80'
},
{
  id: '02',
  title: 'Capital Flow',
  subtitle: 'Autonomous Rent Collection',
  desc: 'Stripe-powered routing instantly reconciles ledgers without human intervention. Intelligent, multi-channel reminders ensure capital moves precisely when it should.',
  image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2000&q=80'
},
{
  id: '03',
  title: 'Operational Integrity',
  subtitle: 'Intelligent Maintenance',
  desc: 'Tenants report issues in seconds. Our routing engine assigns, tracks, and closes tickets while maintaining a rigorous, audit-ready trail for every property.',
  image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=2000&q=80'
},
{
  id: '04',
  title: 'Financial Truth',
  subtitle: 'Institutional Reporting',
  desc: 'Rent roll, P&L, and cash flow generated with mathematical precision. Exportable, flawless, and built for strict institutional compliance from day one.',
  image: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=2000&q=80'
}];


// --- ADVANCED EASING CURVE ---
// This provides that weighty, expensive, ultra-smooth Apple-like feel
const transition = { duration: 0.8, ease: [0.16, 1, 0.3, 1] };

export default function ElegantShifts() {const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [expandedIndex, setExpandedIndex] = useState(0);

  const bgClass = isDarkMode ? 'bg-[#000000]' : 'bg-[#F9F9F9]';
  const textPrimary = isDarkMode ? 'text-white' : 'text-black';
  const textSecondary = isDarkMode ? 'text-white/50' : 'text-black/80';
  const borderClass = isDarkMode ? 'border-white/15' : 'border-black/15';

  return (
    <section className={`min-h-screen py-24 lg:py-40 ${bgClass} transition-colors duration-700`}>
            <div className="max-w-[85rem] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
                
                {/* LEFT: Sticky Editorial Brief */}
                <div className="lg:col-span-4 lg:sticky lg:top-40">
                    <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={transition}>
            
                        <p className={`text-[10px] font-medium tracking-[0.25em] uppercase mb-8 ${textSecondary}`}>
                            {t("system_architecture")}
                        </p>
                        <h2
              className={`text-5xl lg:text-7xl font-light tracking-tight leading-[1.0] mb-8 ${textPrimary}`}
              style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                            {t("refined")} 
              <br /> <span className="italic text-red-700">{t("operations_1")}</span>
                        </h2>
                        <p className={`text-sm leading-relaxed max-w-sm font-light ${textSecondary}`}>
                            {t("a_masterclass_in_property_management_we")}
                        </p>
                    </motion.div>
                </div>

                {/* RIGHT: Fluid Accordion List */}
                <div className="lg:col-span-8 flex flex-col">
                    {/* Top border line */}
                    <div className={`w-full h-px ${isDarkMode ? 'bg-white/15' : 'bg-black/15'}`} />

                    {FEATURES.map((feature, index) => {
            const isOpen = index === expandedIndex;

            return (
              <motion.div
                key={feature.id}
                layout
                onClick={() => setExpandedIndex(index)}
                className={`group cursor-pointer border-b ${borderClass} overflow-hidden`}>
                
                                {/* Header Row */}
                                <motion.div layout className="flex items-center justify-between py-10 lg:py-12">
                                    <div className="flex items-baseline gap-6 lg:gap-12">
                                        <span className={`text-sm font-mono tracking-widest ${textSecondary}`}>
                                            {feature.id}
                                        </span>
                                        <h3
                      className={`text-4xl lg:text-6xl font-light tracking-tight transition-colors duration-500 ${isOpen ? textPrimary : textSecondary} group-hover:${textPrimary}`}
                      style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                      
                                            {feature.title}
                                        </h3>
                                    </div>
                                    
                                    {/* Elegant Animated Plus/Minus */}
                                    <div className={`relative w-4 h-4 flex items-center justify-center ${textSecondary} group-hover:${textPrimary} transition-colors duration-500`}>
                                        <motion.div
                      animate={{ rotate: isOpen ? 180 : 0, opacity: isOpen ? 0 : 1 }}
                      transition={transition}
                      className="absolute w-full h-[1px] bg-current" />
                    
                                        <motion.div
                      animate={{ rotate: isOpen ? 180 : 90 }}
                      transition={transition}
                      className="absolute w-full h-[1px] bg-current" />
                    
                                    </div>
                                </motion.div>

                                {/* Unmasking Content Section */}
                                <AnimatePresence initial={false}>
                                    {isOpen &&
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={transition}>
                    
                                            <div className="pb-12 pt-4 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-end pl-0 lg:pl-20">
                                                
                                                {/* Text Reveal */}
                                                <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ ...transition, delay: 0.1 }}>
                        
                                                    <h4 className={`text-xs font-semibold tracking-widest uppercase mb-4 text-red-700`}>
                                                        {feature.subtitle}
                                                    </h4>
                                                    <p className={`text-base leading-relaxed font-light ${textSecondary}`}>
                                                        {feature.desc}
                                                    </p>
                                                </motion.div>

                                                {/* The Cinematic Image Mask */}
                                                <motion.div
                        initial={{ opacity: 0, clipPath: 'inset(100% 0 0 0)' }}
                        animate={{ opacity: 1, clipPath: 'inset(0% 0 0 0)' }}
                        exit={{ opacity: 0, clipPath: 'inset(100% 0 0 0)' }}
                        transition={transition}
                        className="w-full aspect-[4/3] relative overflow-hidden rounded-sm bg-black/5">
                        
                                                    {/* Internal Image Scale down effect */}
                                                    <motion.img
                          initial={{ scale: 1.2 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 1.1 }}
                          transition={transition}
                          src={feature.image}
                          alt={feature.title}
                          className="w-full h-full object-cover grayscale-[30%] contrast-125" />
                        
                                                </motion.div>

                                            </div>
                                        </motion.div>
                  }
                                </AnimatePresence>
                            </motion.div>);

          })}
                </div>
            </div>
        </section>);

}